/**
 * MedicalDashboardPage - Medical & Doctor Visits Hub
 * 
 * Redesigned dashboard with organized sections:
 * 1. Prepare for Appointment (featured card)
 * 2. You're Ready for Your Appointment (saved preps)
 * 3. Past Appointments & Visit Notes
 * 4. Track Your Health (Symptoms, Questions)
 * 5. My Health Information (grouped rows)
 * 6. Visits & Records
 * 7. Share & Export
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  ClipboardList, 
  MessageSquareText, 
  Thermometer,
  Pill,
  AlertTriangle,
  Activity,
  History,
  Stethoscope,
  Calendar,
  CalendarPlus,
  FileText,
  FileOutput,
  ChevronRight,
  Heart,
  Plus,
  Edit3,
  Clock,
  Loader2,
  CheckCircle2,
  Eye,
  User,
  FolderOpen,
  Check,
  CalendarCheck,
  Bell,
  BellRing,
  Settings2,
  Download,
  Printer
} from 'lucide-react';
import { useDemo } from '@/contexts/DemoContext';
import useSharedMedicalData from '@/hooks/useSharedMedicalData';
import useAppointmentSync from '@/hooks/useAppointmentSync';
import ReminderTimePicker, { formatReminderTime, DEFAULT_REMINDERS } from '@/components/medical/ReminderTimePicker';
import APP_ICONS from '@/utils/appIcons';
import API from '@/utils/api';
import { format, parseISO, isAfter, addDays } from 'date-fns';
import { toast } from 'sonner';
import nativeCalendarService from '@/services/nativeCalendarService';
import { exportMedicalSummaryPDF } from '@/services/medicalExportService';

// Medical module pastel theme - Soft Mint/Sage
const THEME = {
  bgGradient: 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 30%, #F0FDFA 60%, #F5F3FF 100%)',
  headerBg: '#D1FAE5',
  cardBg: 'rgba(255, 255, 255, 0.9)',
  accent: '#059669',
  accentLight: '#D1FAE5',
};

// Helper to format time based on locale
const formatAppointmentTime = (timeString) => {
  if (!timeString) return '';
  
  try {
    // Parse time string (expected format: HH:mm or HH:mm:ss)
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    
    // Use Intl.DateTimeFormat to respect user's locale
    return new Intl.DateTimeFormat(navigator.language || 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: undefined, // Let browser decide based on locale
    }).format(date);
  } catch {
    return timeString;
  }
};

// Helper to determine if prep is truly "ready" (all steps completed)
const isPrepReady = (prep) => {
  // A prep is "ready" when:
  // 1. It has a doctor name or doctorId
  // 2. It has an appointment date
  // 3. Status is explicitly 'ready' OR was saved with complete data
  if (prep.status === 'ready') return true;
  if (prep.status === 'completed') return true;
  
  // If status is draft, check if minimum required fields are filled
  const hasDoctor = prep.doctorName || prep.doctorId;
  const hasDate = prep.appointmentDate;
  const hasReason = prep.reason;
  
  // Only mark as ready if explicitly saved as ready
  return prep.status === 'ready';
};

const MedicalDashboardPage = () => {
  const navigate = useNavigate();
  const { isDemoMode, demoData } = useDemo();
  const { 
    medications, 
    allergies, 
    conditions, 
    doctors,
    surgeries,
    loading 
  } = useSharedMedicalData();

  // Appointment sync hook for calendar and notifications
  const { syncAppointment, unsyncAppointment, syncing, isSynced } = useAppointmentSync();

  // Scroll state for compact header
  const [isScrolled, setIsScrolled] = useState(false);
  const mainRef = useRef(null);

  // Appointment preparations state
  const [appointmentPreps, setAppointmentPreps] = useState([]);
  const [prepsLoading, setPrepsLoading] = useState(false);
  const [symptoms, setSymptoms] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [addingToCalendar, setAddingToCalendar] = useState(null);
  
  // Reminder picker state
  const [reminderPickerOpen, setReminderPickerOpen] = useState(false);
  const [selectedPrepForReminders, setSelectedPrepForReminders] = useState(null);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Handle scroll for compact header
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch appointment preparations
  useEffect(() => {
    const fetchData = async () => {
      if (isDemoMode) return;
      
      setPrepsLoading(true);
      try {
        const [prepsRes, symptomsRes, questionsRes] = await Promise.all([
          API.get('/medical/appointment-preps'),
          API.get('/medical/symptoms').catch(() => ({ data: [] })),
          API.get('/medical/questions').catch(() => ({ data: [] })),
        ]);
        setAppointmentPreps(prepsRes.data || []);
        setSymptoms(symptomsRes.data || []);
        setQuestions(questionsRes.data || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setPrepsLoading(false);
      }
    };
    
    fetchData();
  }, [isDemoMode]);

  // Get upcoming preparations (future dates, not completed)
  const getUpcomingPreps = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return appointmentPreps
      .filter(prep => {
        if (prep.status === 'completed') return false;
        try {
          const prepDate = parseISO(prep.appointmentDate);
          return isAfter(prepDate, addDays(today, -1));
        } catch {
          return false;
        }
      })
      .sort((a, b) => {
        try {
          return parseISO(a.appointmentDate) - parseISO(b.appointmentDate);
        } catch {
          return 0;
        }
      });
  };

  // Get past/completed appointments
  const getPastAppointments = () => {
    return appointmentPreps
      .filter(prep => prep.status === 'completed')
      .sort((a, b) => {
        try {
          return parseISO(b.appointmentDate) - parseISO(a.appointmentDate);
        } catch {
          return 0;
        }
      });
  };

  // Get counts
  const getCounts = () => {
    if (isDemoMode && demoData?.medical) {
      return {
        medications: demoData.medical.medications?.length || 0,
        allergies: demoData.medical.allergies?.length || 0,
        conditions: demoData.medical.conditions?.length || 0,
        doctors: demoData.medical.doctors?.length || 0,
        surgeries: demoData.medical.surgeries?.length || 0,
      };
    }
    return {
      medications: medications?.length || 0,
      allergies: allergies?.length || 0,
      conditions: conditions?.length || 0,
      doctors: doctors?.length || 0,
      surgeries: surgeries?.length || 0,
    };
  };

  // Get active medications count
  const getActiveMedications = () => {
    if (isDemoMode && demoData?.medical?.medications) {
      return demoData.medical.medications.filter(m => m.is_active !== false).length;
    }
    return medications?.filter(m => m.is_active !== false).length || 0;
  };

  // Get active symptoms
  const getActiveSymptoms = () => {
    return symptoms.filter(s => !s.resolved && !s.endDate && !s.end_date).length;
  };

  // Get unanswered questions
  const getUnansweredQuestions = () => {
    return questions.filter(q => !q.answered).length;
  };

  // Open reminder picker for custom times
  const openReminderPicker = (prep, e) => {
    e?.stopPropagation();
    setSelectedPrepForReminders(prep);
    setReminderPickerOpen(true);
  };

  // Handle custom reminder times save
  const handleSaveCustomReminders = async (reminderTimes) => {
    if (!selectedPrepForReminders) return;
    
    const prep = selectedPrepForReminders;
    setAddingToCalendar(prep.id);
    
    try {
      const result = await syncAppointment(prep, { 
        reminderTimes,
        addToCalendar: true,
        scheduleReminders: true,
      });
      
      if (result.success) {
        setAppointmentPreps(prev => 
          prev.map(p => p.id === prep.id ? {
            ...p,
            native_event_id: result.calendar?.eventId || p.native_event_id,
            reminder_times: reminderTimes,
            reminder_notification_ids: result.notifications?.scheduled ? 
              (p.reminder_notification_ids || []).concat([result.notifications.scheduled]) : 
              p.reminder_notification_ids,
          } : p)
        );
      }
    } finally {
      setAddingToCalendar(null);
      setSelectedPrepForReminders(null);
    }
  };

  // Sync appointment to calendar AND schedule reminders (with default times)
  const handleSyncAppointment = useCallback(async (prep, e) => {
    e?.stopPropagation();
    
    setAddingToCalendar(prep.id);
    
    try {
      const result = await syncAppointment(prep);
      
      if (result.success) {
        // Update local state with sync info
        setAppointmentPreps(prev => 
          prev.map(p => p.id === prep.id ? {
            ...p,
            native_event_id: result.calendar?.eventId || p.native_event_id,
            reminder_notification_ids: result.notifications?.scheduled ? 
              (p.reminder_notification_ids || []).concat([result.notifications.scheduled]) : 
              p.reminder_notification_ids,
          } : p)
        );
      }
    } finally {
      setAddingToCalendar(null);
    }
  }, [syncAppointment]);

  // Legacy handler for just adding to calendar (kept for backwards compatibility)
  const handleAddToCalendar = useCallback(async (prep, e) => {
    e?.stopPropagation();
    
    if (prep.native_event_id) {
      toast.info('This appointment is already in your calendar');
      return;
    }

    setAddingToCalendar(prep.id);
    
    try {
      // Check if native calendar is available
      const isAvailable = nativeCalendarService.isNativeCalendarAvailable();
      
      if (!isAvailable) {
        // Fallback for web - create ICS file
        const startDate = parseISO(prep.appointmentDate);
        if (prep.appointmentTime) {
          const [hours, minutes] = prep.appointmentTime.split(':');
          startDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        }
        
        const endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1); // Default 1 hour duration
        
        const icsContent = [
          'BEGIN:VCALENDAR',
          'VERSION:2.0',
          'PRODID:-//Forget Me Not//Medical//EN',
          'BEGIN:VEVENT',
          `DTSTART:${format(startDate, "yyyyMMdd'T'HHmmss")}`,
          `DTEND:${format(endDate, "yyyyMMdd'T'HHmmss")}`,
          `SUMMARY:${prep.doctorName || 'Doctor Appointment'}`,
          `DESCRIPTION:${prep.reason || 'Medical appointment'}`,
          prep.specialty ? `LOCATION:${prep.specialty}` : '',
          'END:VEVENT',
          'END:VCALENDAR'
        ].filter(Boolean).join('\r\n');
        
        const blob = new Blob([icsContent], { type: 'text/calendar' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `appointment-${prep.id}.ics`;
        a.click();
        URL.revokeObjectURL(url);
        
        toast.success('Calendar file downloaded');
        return;
      }

      // Request calendar permission
      const { granted: hasPermission } = await nativeCalendarService.requestCalendarPermission();
      if (!hasPermission) {
        toast.error('Calendar permission denied');
        return;
      }

      // Create calendar event
      const startDate = parseISO(prep.appointmentDate);
      if (prep.appointmentTime) {
        const [hours, minutes] = prep.appointmentTime.split(':');
        startDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      }
      
      const endDate = new Date(startDate);
      endDate.setHours(endDate.getHours() + 1);

      const eventData = {
        title: prep.doctorName || 'Doctor Appointment',
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        notes: [
          prep.reason ? `Reason: ${prep.reason}` : '',
          prep.specialty ? `Specialty: ${prep.specialty}` : '',
        ].filter(Boolean).join('\n'),
        location: prep.specialty || '',
      };

      const result = await nativeCalendarService.createNativeEvent(eventData);
      
      if (result.success && result.eventId) {
        // Update the prep with the native event ID
        await API.put(`/medical/appointment-preps/${prep.id}`, {
          native_event_id: result.eventId,
        });
        
        // Update local state
        setAppointmentPreps(prev => 
          prev.map(p => p.id === prep.id ? { ...p, native_event_id: result.eventId } : p)
        );
        
        toast.success('Added to calendar');
      }
    } catch (error) {
      console.error('Failed to add to calendar:', error);
      toast.error('Failed to add to calendar');
    } finally {
      setAddingToCalendar(null);
    }
  }, []);

  // Handle export medical summary PDF
  const handleExportSummary = useCallback(async () => {
    setIsExporting(true);
    try {
      await exportMedicalSummaryPDF({ isDemoMode, demoData });
      if (!isDemoMode) {
        toast.success('Medical summary downloaded');
      }
    } catch (error) {
      console.error('Failed to export summary:', error);
      toast.error('Failed to download summary. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [isDemoMode, demoData]);

  const counts = getCounts();
  const upcomingPreps = getUpcomingPreps();
  const pastAppointments = getPastAppointments();
  const activeMedications = getActiveMedications();
  const activeSymptoms = getActiveSymptoms();
  const unansweredQuestions = getUnansweredQuestions();

  // Get status badge for prep
  const getStatusBadge = (prep) => {
    const isReady = isPrepReady(prep);
    
    if (isReady) {
      return (
        <Badge 
          variant="outline" 
          className="text-xs bg-green-100 text-green-700 border-green-200"
        >
          Ready
        </Badge>
      );
    }
    
    return (
      <Badge 
        variant="outline" 
        className="text-xs bg-amber-100 text-amber-700 border-amber-200"
      >
        Draft
      </Badge>
    );
  };

  // Get section heading based on prep statuses
  const getUpcomingSectionTitle = () => {
    const readyCount = upcomingPreps.filter(isPrepReady).length;
    const draftCount = upcomingPreps.length - readyCount;
    
    if (readyCount > 0 && draftCount === 0) {
      return "You're Ready for Your Appointment";
    } else if (draftCount > 0 && readyCount === 0) {
      return "Upcoming Appointments";
    } else {
      return "Upcoming Appointments";
    }
  };

  return (
    <div 
      className="min-h-screen pb-28"
      style={{ background: THEME.bgGradient }}
      data-testid="medical-dashboard-page"
      ref={mainRef}
    >
      {/* Compact Sticky Header */}
      <header 
        className={`sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-emerald-100 transition-all duration-200 ${
          isScrolled ? 'py-2' : 'py-3'
        }`}
      >
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="p-2 -ml-2 flex-shrink-0"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </Button>
            
            <div 
              className={`rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                isScrolled ? 'w-8 h-8' : 'w-10 h-10'
              }`} 
              style={{ backgroundColor: THEME.headerBg }}
            >
              <img 
                src={APP_ICONS.medicalInfo} 
                alt="" 
                className={`object-contain transition-all duration-200 ${
                  isScrolled ? 'w-5 h-5' : 'w-6 h-6'
                }`}
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <h1 className={`font-semibold text-slate-800 transition-all duration-200 ${
                isScrolled ? 'text-base' : 'text-lg'
              }`}>
                Medical & Doctor Visits
              </h1>
              {!isScrolled && (
                <p className="text-xs text-slate-500 truncate">
                  Prepare for appointments & organize your health
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        
        {/* SECTION 1: Quick Access - Prepare for Appointment */}
        <section>
          <Card 
            className="cursor-pointer transition-all hover:shadow-lg active:scale-[0.99] ring-2 ring-emerald-200 ring-offset-1 overflow-hidden"
            onClick={() => navigate('/medical/prepare')}
            data-testid="prepare-appointment-card"
          >
            <CardContent className="p-0">
              <div className="p-5 flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                  <ClipboardList className="h-7 w-7 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-slate-800 text-lg">Prepare for Appointment</h2>
                  <p className="text-sm text-slate-500">Get ready for your next doctor visit</p>
                </div>
                <ChevronRight className="h-6 w-6 text-emerald-400 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        </section>

        {/* SECTION 2: Upcoming Appointments */}
        {upcomingPreps.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                {getUpcomingSectionTitle()}
              </h2>
              {upcomingPreps.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/medical/appointments')}
                  className="text-emerald-600 h-auto py-1 text-xs"
                >
                  View All ({upcomingPreps.length})
                </Button>
              )}
            </div>
            <div className="space-y-3">
              {upcomingPreps.map((prep) => (
                <Card 
                  key={prep.id}
                  className="border-emerald-100 bg-emerald-50/50"
                  data-testid={`upcoming-prep-${prep.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-medium text-slate-800">
                            {prep.doctorName || 'Appointment'}
                          </h3>
                          {getStatusBadge(prep)}
                        </div>
                        <p className="text-sm text-slate-600">
                          {prep.appointmentDate && format(parseISO(prep.appointmentDate), 'EEEE, MMMM d, yyyy')}
                          {prep.appointmentTime && ` at ${formatAppointmentTime(prep.appointmentTime)}`}
                        </p>
                        {prep.reason && (
                          <p className="text-sm text-slate-500 mt-1">Reason: {prep.reason}</p>
                        )}
                        
                        {/* Action buttons */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/medical/appointment/${prep.id}`);
                            }}
                            className="text-xs h-8"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/medical/prepare?id=${prep.id}`);
                            }}
                            className="text-xs h-8"
                          >
                            <Edit3 className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => handleSyncAppointment(prep, e)}
                            disabled={addingToCalendar === prep.id || syncing === prep.id}
                            className={`text-xs h-8 ${
                              (prep.native_event_id && prep.reminder_notification_ids?.length)
                                ? 'text-green-600' 
                                : prep.native_event_id || prep.reminder_notification_ids?.length
                                ? 'text-amber-600'
                                : 'text-blue-600 hover:text-blue-700'
                            }`}
                          >
                            {(addingToCalendar === prep.id || syncing === prep.id) ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (prep.native_event_id && prep.reminder_notification_ids?.length) ? (
                              <BellRing className="h-3 w-3 mr-1" />
                            ) : (
                              <Bell className="h-3 w-3 mr-1" />
                            )}
                            {(prep.native_event_id && prep.reminder_notification_ids?.length)
                              ? 'Synced'
                              : prep.native_event_id 
                              ? 'Add Reminders'
                              : 'Sync & Remind'}
                          </Button>
                          
                          {/* Custom Reminder Times Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => openReminderPicker(prep, e)}
                            disabled={addingToCalendar === prep.id}
                            className="text-xs h-8 text-slate-500 hover:text-slate-700 px-2"
                            title="Customize reminder times"
                          >
                            <Settings2 className="h-3.5 w-3.5" />
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/medical/appointment/${prep.id}/complete`);
                            }}
                            className="text-xs h-8 text-emerald-600 hover:text-emerald-700"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Mark Complete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Empty state when no upcoming preps */}
        {!prepsLoading && upcomingPreps.length === 0 && (
          <section>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-sm text-slate-500">No appointment preparation saved yet.</p>
              <Button
                variant="link"
                className="text-emerald-600 text-sm p-0 h-auto mt-1"
                onClick={() => navigate('/medical/prepare')}
              >
                Prepare for your next visit
              </Button>
            </div>
          </section>
        )}

        {/* SECTION 3: Past Appointments & Visit Notes */}
        <section>
          <Card 
            className="cursor-pointer hover:shadow-md transition-all active:scale-[0.99]"
            onClick={() => navigate('/medical/appointments?view=past')}
            data-testid="past-appointments-row"
          >
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <History className="h-5 w-5 text-slate-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-slate-800 text-sm">Past Appointments & Visit Notes</h3>
                <p className="text-xs text-slate-500">
                  {pastAppointments.length > 0 
                    ? `${pastAppointments.length} completed visit${pastAppointments.length !== 1 ? 's' : ''}`
                    : 'No completed visits yet'}
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
            </CardContent>
          </Card>
          
          {/* Show recent past appointments inline */}
          {pastAppointments.length > 0 && (
            <div className="mt-2 space-y-2">
              {pastAppointments.slice(0, 2).map((appt) => (
                <div 
                  key={appt.id}
                  className="p-3 bg-white rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors flex items-center gap-3"
                  onClick={() => navigate(`/medical/appointment/${appt.id}`)}
                  data-testid={`past-appt-${appt.id}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-4 w-4 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-700">
                        {appt.appointmentDate && format(parseISO(appt.appointmentDate), 'MMM d, yyyy')}
                      </span>
                      <span className="text-slate-400">—</span>
                      <span className="text-sm text-slate-600 truncate">{appt.doctorName || 'Doctor Visit'}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-500 truncate">{appt.reason || 'Visit'}</span>
                      {appt.followUpNeeded && (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                          Follow-up
                        </Badge>
                      )}
                      {appt.visitNotes && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                          Notes
                        </Badge>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* SECTION 4: Track Your Health */}
        <section>
          <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Activity className="h-4 w-4 text-orange-500" />
            Track Your Health
          </h2>
          <div className="space-y-2">
            {/* Symptoms */}
            <Card 
              className="cursor-pointer hover:shadow-md transition-all active:scale-[0.99]"
              onClick={() => navigate('/medical/symptoms')}
              data-testid="symptoms-row"
            >
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                  <Thermometer className="h-5 w-5 text-orange-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-800 text-sm">Symptoms & Concerns</h3>
                  <p className="text-xs text-slate-500">
                    {activeSymptoms > 0 
                      ? `${activeSymptoms} active · Track how you're feeling`
                      : 'Track health changes over time'}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
              </CardContent>
            </Card>

            {/* Questions */}
            <Card 
              className="cursor-pointer hover:shadow-md transition-all active:scale-[0.99]"
              onClick={() => navigate('/medical/questions')}
              data-testid="questions-row"
            >
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <MessageSquareText className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-800 text-sm">Questions for My Doctor</h3>
                  <p className="text-xs text-slate-500">
                    {unansweredQuestions > 0 
                      ? `${unansweredQuestions} to ask · Don't forget important questions`
                      : 'Prepare questions for your visit'}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
              </CardContent>
            </Card>
          </div>
        </section>

        {/* SECTION 5: My Health Information */}
        <section>
          <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Heart className="h-4 w-4 text-red-500" />
            My Health Information
          </h2>
          <Card>
            <CardContent className="p-0 divide-y divide-slate-100">
              {/* Medications */}
              <div 
                className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => navigate('/medical/records?tab=medications')}
                data-testid="medications-row"
              >
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Pill className="h-5 w-5 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-800 text-sm">Medications</h3>
                  <p className="text-xs text-slate-500">
                    {counts.medications > 0 
                      ? `${activeMedications} active${counts.medications > activeMedications ? ` · ${counts.medications - activeMedications} as needed` : ''}`
                      : 'No medications recorded'}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
              </div>

              {/* Allergies */}
              <div 
                className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => navigate('/medical/records?tab=allergies')}
                data-testid="allergies-row"
              >
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-800 text-sm">Allergies</h3>
                  <p className="text-xs text-slate-500">
                    {counts.allergies > 0 
                      ? `${counts.allergies} recorded allergies`
                      : 'No allergies recorded'}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
              </div>

              {/* Medical Conditions */}
              <div 
                className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => navigate('/medical/records?tab=conditions')}
                data-testid="conditions-row"
              >
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Activity className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-800 text-sm">Medical Conditions</h3>
                  <p className="text-xs text-slate-500">
                    {counts.conditions > 0 
                      ? `${counts.conditions} active conditions`
                      : 'No conditions recorded'}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
              </div>

              {/* Doctors & Specialists */}
              <div 
                className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => navigate('/medical/records?tab=doctors')}
                data-testid="doctors-row"
              >
                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                  <Stethoscope className="h-5 w-5 text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-800 text-sm">Doctors & Specialists</h3>
                  <p className="text-xs text-slate-500">
                    {counts.doctors > 0 
                      ? `${counts.doctors} providers`
                      : 'No providers recorded'}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        </section>

        {/* SECTION 6: Visits & Records */}
        <section>
          <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <FolderOpen className="h-4 w-4 text-cyan-500" />
            Visits & Records
          </h2>
          <Card>
            <CardContent className="p-0 divide-y divide-slate-100">
              {/* All Appointments */}
              <div 
                className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => navigate('/medical/appointments')}
                data-testid="appointments-row"
              >
                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-800 text-sm">Appointments</h3>
                  <p className="text-xs text-slate-500">
                    {upcomingPreps.length > 0 || pastAppointments.length > 0
                      ? `${upcomingPreps.length} upcoming · ${pastAppointments.length} previous`
                      : 'No appointments recorded'}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
              </div>

              {/* Medical History */}
              <div 
                className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => navigate('/medical/records?tab=history')}
                data-testid="history-row"
              >
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <History className="h-5 w-5 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-800 text-sm">Medical History</h3>
                  <p className="text-xs text-slate-500">
                    {counts.surgeries > 0 
                      ? `${counts.surgeries} surgeries & procedures`
                      : 'Surgeries, hospitalizations & events'}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
              </div>

              {/* Test Results & Documents */}
              <div 
                className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => navigate('/medical/records?tab=labs')}
                data-testid="documents-row"
              >
                <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-cyan-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-800 text-sm">Test Results & Documents</h3>
                  <p className="text-xs text-slate-500">Lab results, imaging & medical files</p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        </section>

        {/* SECTION 7: Share & Export */}
        <section>
          <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <FileOutput className="h-4 w-4 text-blue-500" />
            Share & Export
          </h2>
          <div className="space-y-2">
            {/* Export Medical Summary - NEW PRIMARY FEATURE */}
            <Card 
              className="cursor-pointer transition-all hover:shadow-md ring-2 ring-blue-200 ring-offset-1 bg-gradient-to-br from-blue-50 to-indigo-50"
              onClick={handleExportSummary}
              data-testid="export-summary-card"
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                  {isExporting ? (
                    <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                  ) : (
                    <Download className="h-6 w-6 text-blue-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-800 text-base">Export Medical Summary</h3>
                  <p className="text-xs text-slate-600">
                    Download a printable PDF with all medications, conditions, and allergies for doctor visits
                  </p>
                </div>
                <Printer className="h-5 w-5 text-blue-400 flex-shrink-0" />
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer transition-all hover:shadow-md"
              onClick={() => navigate('/medical/summary')}
              data-testid="doctor-summary-card"
            >
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-800 text-sm">Doctor Summary</h3>
                  <p className="text-xs text-slate-500">
                    Printable overview of your health information
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer transition-all hover:shadow-md"
              onClick={() => navigate('/medical/records?tab=profile')}
              data-testid="medical-profile-card"
            >
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <User className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-800 text-sm">Medical Profile</h3>
                  <p className="text-xs text-slate-500">
                    Basic info, blood type, emergency contact
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Emergency Binder Link */}
        <section className="pt-2">
          <Card 
            className="cursor-pointer transition-all hover:shadow-md border-amber-200 bg-amber-50/50"
            onClick={() => navigate('/emergency-binder')}
            data-testid="emergency-binder-link"
          >
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-100">
                <img 
                  src={APP_ICONS.emergencyBinder} 
                  alt="" 
                  className="w-5 h-5 object-contain"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-amber-800 text-sm">Emergency Binder</h3>
                <p className="text-xs text-amber-600">
                  Your medical info is also in your Emergency Binder
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-amber-400 flex-shrink-0" />
            </CardContent>
          </Card>
        </section>
      </main>
      
      {/* Custom Reminder Time Picker Dialog */}
      <ReminderTimePicker
        isOpen={reminderPickerOpen}
        onClose={() => {
          setReminderPickerOpen(false);
          setSelectedPrepForReminders(null);
        }}
        selectedTimes={selectedPrepForReminders?.reminder_times || DEFAULT_REMINDERS}
        onSave={handleSaveCustomReminders}
        title={`Reminders for ${selectedPrepForReminders?.doctorName || 'Appointment'}`}
      />
    </div>
  );
};

export default MedicalDashboardPage;
